/**
 * 店家篩選 Mixin
 * 提供距離篩選、地址篩選等共用邏輯
 * 可被 Delivery.vue, Togo.vue, Shopping.vue 等元件共用
 *
 * @version 1.0.0
 */
export default {
  data() {
    return {
      // 定位相關
      userCoords: null,              // 使用者座標 { lat, lng }
      gpsLocation: null,             // GPS 定位點（相容現有程式碼）

      // 篩選相關
      filterMode: 'both',            // 'address': 地址篩選, 'radius': 半徑篩選, 'both': 兩者皆可(OR)
      filterRadius: 15,              // 篩選半徑（公里），預設 15km
      filterCenter: null,            // 篩選中心點 { lat, lng }

      // UI 狀態
      distanceFilterActive: false    // 是否啟用距離篩選
    };
  },
  computed: {
    /**
     * GPS 定位是否處於啟用狀態
     * @returns {Boolean}
     */
    isGpsActive() {
      return !!this.gpsLocation && this.distanceFilterActive;
    }
  },
  methods: {
    /**
     * 用距離篩選店家（主要方法）
     * @param {Object} userCoords - 使用者座標 { lat, lng }
     * @param {Object} options - 篩選選項 { limit, maxRadius }
     * @returns {Array} 篩選後的店家列表（按距離排序）
     */
    filterByDistance(userCoords, options = {}) {
      const { limit = 30, maxRadius = this.filterRadius } = options;

      // 更新狀態
      this.userCoords = userCoords;
      this.filterCenter = userCoords;
      this.gpsLocation = userCoords; // 相容現有程式碼
      this.distanceFilterActive = true;

      console.log(`[filterByDistance] Starting filter - coords: (${userCoords.lat}, ${userCoords.lng}), maxRadius: ${maxRadius}km, limit: ${limit}`);

      // 1. 取得當前分類的所有店家
      const currentShops = this.getCurrentShops();
      console.log(`[filterByDistance] currentShops.length: ${currentShops.length}`);

      // 2. 計算每間店家的距離並篩選
      let shopsWithoutCoords = 0;
      const shopsWithDistance = currentShops
        .map(shop => {
          const shopCopy = { ...shop };
          if (shop.lat && shop.lng) {
            shopCopy.distance = this.calculateDistance(
              userCoords.lat,
              userCoords.lng,
              shop.lat,
              shop.lng
            );
          } else {
            shopCopy.distance = null;
            shopsWithoutCoords++;
          }
          return shopCopy;
        })
        .filter(shop => {
          // 必須要有座標 (距離資訊)
          if (shop.distance === null) return false;

          // 只有在 maxRadius > 0 時才過濾範圍
          if (maxRadius > 0 && shop.distance > maxRadius) return false;

          return true;
        })
        .sort((a, b) => a.distance - b.distance); // 按距離排序（近到遠）

      console.log(`[filterByDistance] Shops without coords: ${shopsWithoutCoords}, shops with coords: ${currentShops.length - shopsWithoutCoords}, after filter: ${shopsWithDistance.length}`);

      // 3. 限制數量
      const limitedShops = limit && limit > 0 ? shopsWithDistance.slice(0, limit) : shopsWithDistance;

      console.log(`[filterByDistance] Final result: ${limitedShops.length} shops (limit: ${limit}, maxRadius: ${maxRadius > 0 ? maxRadius + 'km' : 'no limit'})`);

      // 4. 儲存結果（可選，用於其他元件訪問）
      this.filteredShopsByDistance = limitedShops;

      // 5. 儲存使用者位置到 localStorage
      this.saveUserLocation(userCoords);

      return limitedShops;
    },

    /**
     * 取得當前分類的店家列表
     * @returns {Array} 店家陣列
     */
    getCurrentShops() {
      // 假設所有元件都有 shops 和 selectedCategory
      if (!this.shops || !this.selectedCategory) {
        console.warn('shops or selectedCategory not defined');
        return [];
      }

      return this.shops[this.selectedCategory] || [];
    },

    /**
     * 計算兩點之間的距離（Haversine 公式）
     * @param {Number} lat1 起點緯度
     * @param {Number} lng1 起點經度
     * @param {Number} lat2 終點緯度
     * @param {Number} lng2 終點經度
     * @returns {Number} 距離（公里）
     */
    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371; // 地球半徑（公里）
      const dLat = this.toRadians(lat2 - lat1);
      const dLng = this.toRadians(lng2 - lng1);

      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(this.toRadians(lat1)) *
                Math.cos(this.toRadians(lat2)) *
                Math.sin(dLng/2) * Math.sin(dLng/2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    },

    /**
     * 角度轉弧度
     */
    toRadians(degrees) {
      return degrees * Math.PI / 180;
    },

    /**
     * 組合篩選：地址 + 距離（提供給 filteredShops computed 使用）
     * @param {Array} shops - 店家列表
     * @param {Object} filters - 篩選條件 { city, district, coords, radius, mode }
     * @returns {Array} 篩選後的店家列表
     */
    applyMixedFilter(shops, filters = {}) {
      const {
        city = this.cityFilter,
        district = this.districtFilter,
        coords = this.filterCenter || this.gpsLocation,
        radius = this.filterRadius,
        limit = 60,
        mode = this.filterMode
      } = filters;

      if (!shops || shops.length === 0) {
        return [];
      }

      // 1. 預先為所有店家計算距離 (如果有傳入座標)
      let shopsWithInfo = shops;
      if (coords && coords.lat && coords.lng) {
        shopsWithInfo = shops.map(shop => {
          const shopCopy = { ...shop };
          if (shop.lat && shop.lng) {
            shopCopy.distance = this.calculateDistance(
              coords.lat,
              coords.lng,
              shop.lat,
              shop.lng
            );
          } else {
            shopCopy.distance = null;
          }
          return shopCopy;
        });
      }

      // 2. 地址篩選
      let addressFiltered = [];
      if (city || district) {
        addressFiltered = shopsWithInfo.filter(shop => {
          if (!shop.address) return false;

          const cityMatch = !city || shop.address.includes(city);
          const districtMatch = !district || shop.address.includes(district);
          
          return cityMatch && districtMatch;
        });
      }

      // 3. 距離篩選 (只有在 radius > 0 時才執行物理過濾)
      let distanceFiltered = [];
      if (coords && coords.lat && coords.lng) {
        if (radius > 0) {
          distanceFiltered = shopsWithInfo.filter(shop => 
            shop.distance !== null && shop.distance <= radius
          );
        } else {
          // radius 為 0 或未提供，視為關閉半徑限制，保留所有有距離資訊的店家
          distanceFiltered = shopsWithInfo.filter(shop => shop.distance !== null);
        }
      }

      let result = [];

      // 4. 根據 mode 決定組合邏輯
      if (mode === 'both' && (city || district) && (coords && coords.lat && coords.lng)) {
        // 優先使用地址篩選結果
        result = [...addressFiltered];

        // 若地址篩選結果少於 limit，補充距離篩選的店家
        if (limit && limit > 0 && result.length < limit) {
          // 先排序距離篩選結果（按距離由近到遠）
          const sortedDistanceFiltered = [...distanceFiltered].sort((a, b) => {
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          });

          // 補充尚未在地址篩選結果中的店家，直到達到 limit
          for (const shop of sortedDistanceFiltered) {
            if (result.length >= limit) break;

            // 檢查是否已在結果中（避免重複）
            if (!result.find(s => s.id === shop.id)) {
              result.push(shop);
            }
          }
        }
      } else if (mode === 'address' || (!coords && (city || district))) {
        // 有篩選條件才過濾，否則（選全部縣市）顯示全部
        result = (city || district) ? addressFiltered : [...shopsWithInfo];
      } else if (mode === 'radius' || (coords && !city && !district)) {
        result = distanceFiltered;
      } else {
        // 預設邏輯：優先取有結果的
        if (addressFiltered.length > 0) {
          result = addressFiltered;
        } else if (distanceFiltered.length > 0) {
          result = distanceFiltered;
        } else {
          result = [];
        }
      }

      // 5. 非地址模式才依距離排序
      if (mode !== 'address' && coords && coords.lat && coords.lng) {
        result.sort((a, b) => {
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        });
      }

      // 6. 若有 limit 限制數量
      if (limit && limit > 0 ) {
        result = result.slice(0, limit);
      }
      return result;
    },

    /**
     * 儲存使用者位置到 localStorage
     * @param {Object} coords { lat, lng }
     */
    saveUserLocation(coords) {
      try {
        localStorage.setItem('userLocation', JSON.stringify({
          lat: coords.lat,
          lng: coords.lng,
          timestamp: Date.now()
        }));
        console.log('User location saved to localStorage');
      } catch (e) {
        console.error('Failed to save user location:', e);
      }
    },

    /**
     * 從 localStorage 讀取使用者位置
     * @returns {Object|null} { lat, lng, timestamp }
     */
    getUserLocation() {
      try {
        const stored = localStorage.getItem('userLocation');
        if (!stored) return null;

        const parsed = JSON.parse(stored);

        // 檢查是否過期（24小時）
        const expiresAt = parsed.timestamp + 24 * 60 * 60 * 1000;
        if (Date.now() > expiresAt) {
          localStorage.removeItem('userLocation');
          return null;
        }

        return parsed;
      } catch (e) {
        console.error('Failed to get user location:', e);
        return null;
      }
    },

    /**
     * 清除距離篩選
     */
    clearDistanceFilter() {
      this.userCoords = null;
      this.filterCenter = null;
      this.gpsLocation = null;
      this.distanceFilterActive = false;
      this.filteredShopsByDistance = null;
      console.log('Distance filter cleared');
    },

    /**
     * 取得距離篩選的統計資訊
     * @returns {Object} { total, withinRadius, withoutCoords }
     */
    getDistanceFilterStats() {
      const currentShops = this.getCurrentShops();
      const total = currentShops.length;
      const withoutCoords = currentShops.filter(s => !s.lat || !s.lng).length;
      const withinRadius = this.filteredShopsByDistance ? this.filteredShopsByDistance.length : 0;

      return {
        total,
        withoutCoords,
        withinRadius,
        filterRadius: this.filterRadius
      };
    }
  }
};
